import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Grid,
  Input,
  Layout,
  Menu,
  Row,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Centre, fetchCentres, setCentreFilter, setCentreShowAll } from "./actions";

import { getFilteredCentreList, getCentreLoading, getFilterName, getShowAll, getCentreError } from "./selectors";
import { getAllCountries } from "app/shared/api/getCountries";
import "./CentreManager.css";
import { PublishedEventType, StatusIcon, useSignalREvent } from "app/helpers/signalRHelper";
import { getIsoFromCountryDto, pickFirstString, tryIso2 } from "./centreFormShared";

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const EXCLUDED_COUNTRIES = new Set<string>(["Australia", "New Zealand"]);
const EXCLUDED_COUNTRIES_LOWER = new Set<string>(
  Array.from(EXCLUDED_COUNTRIES).map((country) => country.trim().toLowerCase())
);
const EXCLUDED_COUNTRY_ISOS = new Set<string>(["AU", "NZ"]);

const isExcludedCountry = (label?: string | null, iso?: string | null): boolean => {
  const normalizedLabel = (label ?? "").trim();
  if (normalizedLabel) {
    if (EXCLUDED_COUNTRIES.has(normalizedLabel)) return true;
    if (EXCLUDED_COUNTRIES_LOWER.has(normalizedLabel.toLowerCase())) return true;
  }
  if (iso) {
    const normalizedIso = iso.trim().toUpperCase();
    if (EXCLUDED_COUNTRY_ISOS.has(normalizedIso)) return true;
  }
  return false;
};

const slugify = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "item";

type CountryGroup = {
  key: string;
  label: string;
  isoCode?: string;
  centres: Centre[];
  originalCount: number;
};

const Page: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [countryNames, setCountryNames] = useState<Record<string, string>>({});
  const [selectedCountryKey, setSelectedCountryKey] = useState<string | null>(null);

  const centres = useSelector(getFilteredCentreList);
  const loading = useSelector(getCentreLoading);
  const error = useSelector(getCentreError);
  const filterText = useSelector(getFilterName);
  const showAll = useSelector(getShowAll);

  const refreshGridEvent = async () => {
    dispatch(fetchCentres() as any);
  };

  const { status, lastEventReceived } = useSignalREvent(
    [PublishedEventType.CentreEventPublished],
    refreshGridEvent
  );

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    dispatch(fetchCentres() as any);
  }, [dispatch]);

  // Restore the last selected country after creating/updating a centre.
  useEffect(() => {
    if (selectedCountryKey) return;
    try {
      const stored = localStorage.getItem("centreManager.selectedCountryKey");
      if (stored && stored.trim()) {
        setSelectedCountryKey(stored.trim());
        localStorage.removeItem("centreManager.selectedCountryKey");
      }
    } catch {
    }
  }, [selectedCountryKey]);

  useEffect(() => {
    let mounted = true;
    getAllCountries()
      .then((countries) => {
        if (!mounted) return;
        const lookup: Record<string, string> = {};
        (countries || []).forEach((country: any) => {
          const iso = getIsoFromCountryDto(country);
          if (iso) {
            const name = pickFirstString(country?.name, country?.displayName, iso) ?? iso;
            lookup[iso] = name;
          }
        });
        setCountryNames(lookup);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const groupedCountries = useMemo<CountryGroup[]>(() => {
    const groups = new Map<string, { label: string; centres: Centre[] }>();

    centres.forEach((centre) => {
      const isoCandidate = pickFirstString(
        (centre as any)?.countryCode,
        (centre as any)?.country,
        centre.country,
        centre.centreAddress?.country
      );
      const iso = tryIso2(isoCandidate);
      const label =
        (iso && countryNames[iso]) ||
        pickFirstString(
          (centre as any)?.countryName,
          centre.country,
          centre.centreAddress?.country,
          isoCandidate
        ) ||
        iso ||
        "Unknown Country";

      const key = iso ? iso : slugify(label);

      if (!groups.has(key)) {
        groups.set(key, { label, centres: [] });
      }
      groups.get(key)!.centres.push(centre);
    });

    return Array.from(groups.entries())
      .map(([key, value]) => {
        const sorted = [...value.centres].sort((a, b) =>
          (a.centreName || "").localeCompare(b.centreName || "", undefined, {
            sensitivity: "base",
          })
        );
        return {
          key,
          label: value.label,
          isoCode: tryIso2(key),
          centres: sorted,
          originalCount: sorted.length,
        };
      })
      .filter((group) => group.centres.length > 0 && !isExcludedCountry(group.label, group.isoCode))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [centres, countryNames]);

  useEffect(() => {
    if (!groupedCountries.length) {
      setSelectedCountryKey(null);
      return;
    }
    if (selectedCountryKey && !groupedCountries.some((c) => c.key === selectedCountryKey)) {
      setSelectedCountryKey(null);
    }
  }, [groupedCountries]);

  const activeCountry = useMemo(() => {
    if (!selectedCountryKey) return null;
    return groupedCountries.find((country) => country.key === selectedCountryKey) ?? null;
  }, [groupedCountries, selectedCountryKey]);

  const selectedCountryCentres = activeCountry?.centres ?? [];
  const visibleCountryCentres = React.useMemo(() => {
    const centresList = activeCountry?.centres ?? [];
    return showAll ? centresList : centresList.filter((centre) => centre.active);
  }, [activeCountry?.centres, showAll]);

  const menuItems = useMemo<MenuProps["items"]>(() => {
    return groupedCountries.map((country) => {
      const hasActive = country.centres.some((c) => c.active);
      const dotColor = hasActive ? "#52c41a" : "#8c8c8c";
      return {
        key: country.key,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: dotColor,
                flexShrink: 0,
              }}
              aria-hidden
            />
            {country.label}{" "}
            <span style={{ color: "#8c8c8c" }}>({country.centres.length})</span>
          </span>
        ),
      };
    });
  }, [groupedCountries]);

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    const target = groupedCountries.find((country) => country.key === key);
    if (!target) return;
    setSelectedCountryKey(target.key);
  };
  const header = useMemo(() => {
    const hasSelection = !!activeCountry;
    const locationText = groupedCountries.length
      ? hasSelection
        ? `Showing centres in ${activeCountry?.label ?? ""}`
        : "Select a country to view its centres"
      : "No centres available";

    return (
      <Flex align="center" justify="space-between" wrap style={{ gap: 12 }}>
        <Space orientation="vertical" size={0}>
          <Space orientation="horizontal">
            <Title level={4} style={{ margin: 0 }}>
              Centre Manager
            </Title>
          </Space>
          <Text type="secondary">{locationText}</Text>
        </Space>
        <Space wrap>
          {hasSelection && (
            <StatusIcon
              status={status}
              lastEventReceived={lastEventReceived}
              unpublishedChanges={
                Array.isArray(selectedCountryCentres) &&
                selectedCountryCentres.some((x) => x?.entityUpdate === true)
              }
              onManualRefresh={refreshGridEvent}
            />
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/centre-manager/add")}
          >
            Add Centre
          </Button>
        </Space>
      </Flex>
    );
  }, [navigate, groupedCountries.length, activeCountry, selectedCountryCentres, status, lastEventReceived]);

  const emptyDescription = filterText
    ? "No centres match your search"
    : activeCountry
      ? "No centres available for this country"
      : groupedCountries.length
        ? "Select a country to view its centres"
        : "No centres available";

  return (
    <Layout style={{ height: '100%', background: '#fff' }}>
      <Sider width={screens.lg ? 260 : 220} theme="light" style={{ borderRight: '1px solid #f0f0f0', background: '#fff' }}>
        <div style={{ padding: 12 }}>
          <Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>Centre</Title>
          <Input.Search
            placeholder="Search centres"
            value={filterText}
            onChange={(e) => dispatch(setCentreFilter(e.target.value))}
            allowClear
          />
          <div className="show-inactive-row">
            <span className="show-inactive-label">Show inactive</span>
            <Switch
             size="small"
              checked={showAll}
              onChange={(val) => dispatch(setCentreShowAll(val))}
            />
          </div>
        </div>
        {loading ? (
          <div style={{ padding: "32px 16px" }}>
            <Space orientation="vertical" align="center" style={{ width: "100%" }}>
              <Spin />
              <Text type="secondary">Loading centres...</Text>
            </Space>
          </div>
        ) : !groupedCountries.length ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              filterText.trim()
                ? "No centres match your search"
                : showAll
                ? "No centres found"
                : "No active centres found"
            }
            style={{ marginTop: 24 }}
          />
        ) : (
          <Menu
            mode="inline"
            selectedKeys={selectedCountryKey ? [selectedCountryKey] : []}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ borderInlineEnd: 0, paddingBottom: 8 }}
          />
        )}
      </Sider>
      <Layout>
        <Content style={{ padding: 16 }}>
          {header}
          <div style={{ marginTop: 12, borderTop: "1px solid #f0f0f0" }} />

          <Spin spinning={loading} description="Loading centres...">
            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              {visibleCountryCentres.map((centre) => {
                const key = centre.id ?? centre.centreCode ?? centre.centreName ?? "";
                const addressParts = [
                  centre.centreAddress?.building,
                  centre.centreAddress?.street,
                  centre.centreAddress?.city,
                  centre.centreAddress?.country,
                ]
                  .map((part) => String(part ?? "").trim())
                  .filter((part) => part.length > 0);

                const locked = centre.entityUpdate;
                const idForRoute = centre.id != null ? String(centre.id) : "";

                return (
                  <Col key={key} xs={24} sm={12} lg={8}>
                    <Card
                      hoverable
                      onClick={() =>
                        locked || !idForRoute ? undefined : navigate(`/centre-manager/${encodeURIComponent(idForRoute)}`, { state: { centre } })
                      }
                      style={{
                        height: "100%",
                        cursor: locked ? "not-allowed" : "pointer",
                        opacity: locked ? 0.6 : 1,
                        backgroundColor: locked ? "#e0e0e0ff" : "white",
                      }}
                      styles={{ body: { minHeight: 200 } }}
                    >
                      <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                        <Flex align="center" justify="space-between">
                          <Text strong>{centre.centreName || "Unnamed Centre"}</Text>
                          <Tag color={centre.active ? "green" : "red"}>{centre.active ? "Active" : "Inactive"}</Tag>
                        </Flex>
                        <Text type="secondary">
                          {centre.centreCode || "No code"}{" "}
                          {centre.centreAddress?.city || centre.country || activeCountry?.label || "Unknown"}
                        </Text>
                        {addressParts.length > 0 && <Text type="secondary">{addressParts.join(", ")}</Text>}
                        {centre.generalEmail && <Text type="secondary">{centre.generalEmail}</Text>}
                        {centre.telephoneNo && <Text type="secondary">{centre.telephoneNo}</Text>}
                      </Space>
                    </Card>
                  </Col>
                );
              })}

              <Col key="add-centre-card" xs={24} sm={12} lg={8}>
                <Card
                  hoverable
                  onClick={() => navigate("/centre-manager/add")}
                  style={{ height: "100%", borderStyle: "dashed" }}
                  styles={{
                    body: {
                      minHeight: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    },
                  }}
                >
                  <Space orientation="vertical" align="center">
                    <PlusOutlined style={{ fontSize: 24 }} />
                    <Text strong>Add New Centre</Text>
                    <Text type="secondary">Create a new centre record</Text>
                  </Space>
                </Card>
              </Col>

              {!visibleCountryCentres.length && !loading && (
                <Col span={24}>
                  <Card variant="borderless" style={{ textAlign: "center" }}>
                    <Empty description={emptyDescription} />
                  </Card>
                </Col>
              )}
            </Row>
          </Spin>
        </Content>
      </Layout>
    </Layout>
  );
};

export default Page;
